import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  Collapse,
  IconButton,
  Img,
  InfoIcon,
  QuantityEditor,
  TrashIcon,
  Typography
} from '@ag1/ui';
import { CartLine } from '@portal/api/shopifyV2/types';
import {
  SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID,
  SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS,
  SHOPIFY_VITAMIN_D3_OTP_VARIANT_ID
} from '@portal/config/base';
import { getFlavorConfigFromVariantId } from '@portal/constants/flavors';
import { ShopDescriptors } from '@portal/processes/ShopV2/ShopV2.types';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import {
  isGrandFatherAGZ,
  isRequiredSubscriptionProduct
} from '@portal/shared/components/V53EditProductsModal/utils';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { getFormattedShopifyId } from '@portal/shared/utils/shopify/ids';
import { getShopifyVariantId } from '@portal/shared/utils/subscriptionV2Helpers';
import { useStoreValue } from '@portal/state/hooks/useStore';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BUNDLE_ITEMS,
  BundleItemType,
  CADENCE_OPTIONS,
  OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE,
  OMEGA_SUBSCRIBE_AND_SAVE_PRICE,
  AGZ_OTP_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE,
  AGZ_OTP_SUBSCRIBE_AND_SAVE_PRICE,
  AG1_POUCH_OR_TRAVEL_PACK_OTP_SUBSCRIBE_AND_SAVE_PRICE
} from '../../constants';
import { calculateFinalPrice, isWelcomeKit, flavorToKey } from '../../util';
import styles from './SiteCartItem.module.scss';
import {
  isPouchOTPVariant,
  isTravelPackOTPVariant
} from '@portal/shared/utils/shopify/isPouchSubscriptionVariant';
import { useHasPouchOrPackSubscription } from '@portal/processes/ShopListProducts/hooks/useHasPouchOrPackSubscription';

interface QuantityBadgeProps {
  quantity: number;
}

const QuantityBadge = ({ quantity }: QuantityBadgeProps) => {
  return (
    <div className={styles.quantityBadge}>
      <Typography variant="caption" size="1" noPadding className={styles.quantityBadgeText}>
        {quantity}
      </Typography>
    </div>
  );
};

interface BundleItemProps {
  item: BundleItemType;
}

// Temporary BundleItem - pending BE data
const BundleItem = ({ item }: BundleItemProps) => {
  const store = useStoreValue();
  const { formatShopifyMoney } = useMoney();
  const { t } = useTranslation('siteCart');
  const { id, title, quantity, image, price: finalPrice, originalPrice: originalTotalPrice } = item;
  const showQuantityBadge = quantity > 1;

  // Flavor Data
  const flavorConfig = getFlavorConfigFromVariantId(id, store);
  const showFlavor = !!flavorConfig;

  const hasDiscounts = true;
  const freeLabel = t('free');

  // Format price with proper internationalization using Money objects directly
  const formatCartPrice = (amount: number, hasDiscountFormatting: boolean = false) => {
    if (amount === 0) return freeLabel;
    return formatShopifyMoney(
      { amount: amount.toString(), currencyCode: 'USD' },
      {
        hideDecimalsForWholeNumbers: hasDiscountFormatting
      }
    );
  };

  return (
    <div className={styles.bundleItem}>
      <div className={styles.imageContainer}>
        <Img src={image} alt={title} className={styles.bundleItemImage} />
        {showQuantityBadge && <QuantityBadge quantity={quantity} />}
      </div>
      <div className={styles.itemDetails}>
        <Typography variant="portalCaption" size="1" noPadding className={styles.bundleItemTitle}>
          {title}
        </Typography>

        {showFlavor && (
          <div className={styles.flavorSection}>
            <div
              className={styles.flavorIndicator}
              style={{ backgroundColor: flavorConfig.color }}
            />
            <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
              {flavorConfig.title}
            </Typography>
          </div>
        )}
      </div>
      <div className={styles.rightSection}>
        <div className={styles.priceContainer}>
          <Typography
            variant="caption"
            size="1"
            noPadding
            className={clsx(
              styles.newPrice,
              formatCartPrice(finalPrice, hasDiscounts) === freeLabel && styles.freePrice
            )}>
            {formatCartPrice(finalPrice, hasDiscounts)}
          </Typography>
          {hasDiscounts && (
            <Typography variant="caption" size="1" noPadding className={styles.oldPrice}>
              {formatCartPrice(originalTotalPrice, true)}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export interface SiteCartItemProps {
  item: CartLine;
  isSubscription: boolean;
}

export const SiteCartItem = ({ item, isSubscription }: SiteCartItemProps) => {
  const { t } = useTranslation('siteCart');
  const freeLabel = t('free');
  const { formatMoney, formatShopifyMoney } = useMoney();
  const store = useStoreValue();

  // Format price using Shopify Money objects directly - cleaner and more accurate
  const formatCartPrice = useCallback(
    (
      moneyObject: { amount: string; currencyCode: string },
      hasDiscountFormatting: boolean = false
    ) => {
      if (Number(moneyObject.amount) === 0) return freeLabel;
      return formatShopifyMoney(moneyObject, {
        hideDecimalsForWholeNumbers: hasDiscountFormatting
      });
    },
    [freeLabel, formatShopifyMoney]
  );

  const {
    isCartUpdating,
    updateCartItemQuantity,
    subscribeToCartItem,
    isDebugMode,
    debugHighlightCartLines,
    checkDebugHighlightProductIds,
    quantityErrors,
    clearQuantityErrors,
    paidSubItems
  } = useSiteCart();

  // Detect if this is an omega product
  const isOmegaProduct = useMemo(() => {
    const variantId = getShopifyVariantId(item.merchandise.id);
    const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store] || {};

    // Check all omega variants: default (one-time), subscriptionPrice, and bundlePrice
    const allOmegaVariantIds = Object.values(omegaVariants).filter(Boolean);
    return allOmegaVariantIds.includes(variantId);
  }, [item.merchandise.id, store]);
  const isAGZOTPProduct = useMemo(() => {
    const variantId = getShopifyVariantId(item.merchandise.id);
    const agzOTPVariants = SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store] || {};

    const allAGZOTPVariantIds = Object.values(agzOTPVariants).filter(Boolean);
    return allAGZOTPVariantIds.includes(variantId);
  }, [item.merchandise.id, store]);

  const isVitaminD3Product = useMemo(() => {
    const variantId = getShopifyVariantId(item.merchandise.id);
    const vitaminD3Variants = SHOPIFY_VITAMIN_D3_OTP_VARIANT_ID[store] || {};

    return variantId === vitaminD3Variants;
  }, [item.merchandise.id, store]);

  const isGrandFatherAgzProduct = useMemo(() => {
    return isGrandFatherAGZ(getShopifyVariantId(item.merchandise.id), store);
  }, [item.merchandise.id, store]);

  // Error state for quantity limit (manual validation errors)
  const [quantityError, setQuantityError] = useState<string | null>(null);

  // Check for cart rules quantity errors (derived state, no useEffect needed)
  const quantityErrorFromRules = useMemo(() => {
    const qtyError = quantityErrors.find((err) => err.merchandiseId === item.merchandise.id);
    if (qtyError) {
      // Add product name to the cart rules error message to match manual validation format
      return `${qtyError.errorMessage.replace('.', '')} for ${item.merchandise.product.title}.`;
    }
    return null;
  }, [quantityErrors, item.merchandise.id, item.merchandise.product.title]);

  // Use either manual error or cart rules error
  const displayError = quantityError || quantityErrorFromRules;

  const { hasDiscounts } = useMemo(() => calculateFinalPrice(item), [item]);
  const isFreeItem = Number(item.cost.totalAmount.amount) === 0;

  // Get max quantity for this item
  const maxQuantity = useMemo(() => {
    // Check for custom max quantity from product metafields first
    const customMaxQuantity = item.merchandise.product.metafields?.max_quantity;
    const parsedQuantity = parseInt(customMaxQuantity ?? '', 10);

    if (!Number.isNaN(parsedQuantity) && parsedQuantity >= 0) {
      return parsedQuantity;
    }

    // Fall back to default max quantity based on subscription type
    return isSubscription ? 3 : 6;
  }, [item.merchandise.product.metafields?.max_quantity, isSubscription]);

  // Product description
  const description = useMemo(
    () => item.merchandise.product.metafields?.[ShopDescriptors.subtitle] || item.merchandise.title,
    [item.merchandise.product.metafields, item.merchandise.title]
  );

  // Metafield original per price override
  const originalPriceMetafield = useMemo(() => {
    const metafield = item.merchandise.product.metafields?.original_price;
    if (!metafield) return '';

    const parsedPrice = metafield.split('$').pop();
    return Number(parsedPrice || 0) * item.quantity;
  }, [item.merchandise.product.metafields?.original_price, item.quantity]);

  // Disabled for P1
  const showStrikethroughPrice = (originalPriceMetafield || hasDiscounts) && isDebugMode;

  // Disabled for P1
  const disallowSubscriptionUpgradeProducts: string[] = [];
  const isProductSubscribeDisabled = disallowSubscriptionUpgradeProducts.some((name) =>
    item.merchandise.product.title.toLocaleLowerCase().includes(name)
  );

  const { hasAnyActiveSubWithPouchOrPackSubscription } = useHasPouchOrPackSubscription();
  // Generic subscribe button visibility - can be made configurable via props if needed
  const showSubscribeButton =
    !isSubscription &&
    !isFreeItem &&
    !isProductSubscribeDisabled &&
    !hasAnyActiveSubWithPouchOrPackSubscription &&
    !isVitaminD3Product &&
    !isGrandFatherAgzProduct;

  // Check if there are AG1 products that trigger bundle pricing for Omega
  const hasAG1ForBundle = useMemo(() => {
    // Check if any paid subscription items are AG1 products (pouches or travel packs)
    return paidSubItems.some((subItem) => {
      const variantId = getShopifyVariantId(subItem.merchandise.id);
      return isRequiredSubscriptionProduct(variantId, store);
    });
  }, [paidSubItems, store]);

  // Disabled for P1
  const [isBundleOpen, setIsBundleOpen] = useState(false);
  const isBundle = BUNDLE_ITEMS.length > 0 && isWelcomeKit(item) && isDebugMode;

  // Check if item is a bundle item
  const isBundleItem =
    item.attributes?.find((attr) => attr.key === '__isBundleItem')?.value === 'true';

  const canChangeQuantity = !isFreeItem && !isBundleItem;

  const showQuantityBadge = canChangeQuantity || item.quantity > 1;
  // P1 - line removal only
  const showRemoveButton = !isDebugMode && !isFreeItem && !canChangeQuantity;

  // Flavor Data
  const flavorConfig = getFlavorConfigFromVariantId(item.merchandise.id, store);
  const showFlavor = !!flavorConfig;

  const isAG1PouchorPackOTP =
    isPouchOTPVariant(item.merchandise.id, store) ||
    isTravelPackOTPVariant(item.merchandise.id, store);
  let flavor = item.merchandise.metafields?.flavor?.toLowerCase();
  const AG1SubPouchVariants = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store];
  const AG1SubTravelPackVariants = SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store];

  // Convert one-time purchase to subscription variant
  const handleSubscribeItem = async () => {
    if (isCartUpdating) return;

    const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store];
    const agzSubVariants = SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS[store];

    const variantId = getShopifyVariantId(item.merchandise.id);
    // Get the 30-day cadence (monthly subscription)
    const monthlyCadence = CADENCE_OPTIONS[store]?.find((c) => c.label.includes('30'));
    if (!monthlyCadence) return;

    // Convert AG1 Pouch or Pack one-time purchase to subscription variant
    if (!isOmegaProduct && isAG1PouchorPackOTP) {
      // Only convert if this is the one-time purchase variant
      if (
        variantId === AG1SubPouchVariants[flavor as keyof typeof AG1SubPouchVariants] ||
        variantId === AG1SubTravelPackVariants[flavor as keyof typeof AG1SubTravelPackVariants]
      )
        return;

      try {
        const variantKey = flavor === 'original' ? 'default' : flavor;
        const variantId = isPouchOTPVariant(item.merchandise.id, store)
          ? AG1SubPouchVariants[variantKey as keyof typeof AG1SubPouchVariants]
          : AG1SubTravelPackVariants[variantKey as keyof typeof AG1SubTravelPackVariants];

        if (!variantId) return;
        const subscriptionMerchandiseId = getFormattedShopifyId('ProductVariant', variantId);

        await subscribeToCartItem(item, subscriptionMerchandiseId, monthlyCadence.value);
      } catch (error) {
        console.error('Error converting to subscription:', error);
      }

      return;
      // Convert Omega or AGZ OTP one-time purchase to subscription variant
    } else if (isOmegaProduct || isAGZOTPProduct) {
      flavor = flavorToKey(flavor || '');

      try {
        const priceType = hasAG1ForBundle ? 'bundlePrice' : 'subscriptionPrice';
        const productVariants = isAGZOTPProduct
          ? agzSubVariants?.[flavor as keyof typeof agzSubVariants]
          : omegaVariants;
        const variantId = productVariants?.[priceType as keyof typeof productVariants] || '';
        if (!variantId) throw new Error('Unable to resolve merchandiseId');
        // Use the dedicated subscribeToCartItem method for atomic conversion
        const subscriptionMerchandiseId = getFormattedShopifyId('ProductVariant', variantId);

        await subscribeToCartItem(item, subscriptionMerchandiseId, monthlyCadence.value);

        // Note: Cart rules will automatically swap to bundle variant if other subscription items exist
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error converting to subscription:', error);
      }
    }
  };

  // Set max quantity error message
  const setMaxQuantityError = () => {
    setQuantityError(
      `Maximum QTY of ${maxQuantity} reached for ${item.merchandise.product.title}.`
    );
  };

  // Handle quantity change with error checking
  const handleQuantityChange = (newQuantity: number) => {
    // Clear any existing errors (both manual and cart rules)
    setQuantityError(null);
    clearQuantityErrors();

    // Check if the new quantity exceeds the limit
    if (newQuantity > maxQuantity) {
      setMaxQuantityError();
      return;
    }

    // Update the cart item quantity
    updateCartItemQuantity(item, newQuantity);
  };

  // Handle when increment button is clicked while disabled
  const handleIncrementDisabled = () => {
    // Clear cart rules errors since this is a manual interaction
    clearQuantityErrors();
    setMaxQuantityError();
  };

  const saveAndSubscribe = useMemo(
    () => (item: CartLine) => {
      const MIN_BUNDLE_QTY = 3;
      const qty = item.quantity ?? 0;
      if (qty > MIN_BUNDLE_QTY) return t('subscriptionSavings');
      const currency = item.cost?.totalAmount?.currencyCode ?? 'USD';

      const defaultAGZUnitPrice = hasAG1ForBundle
        ? AGZ_OTP_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE
        : AGZ_OTP_SUBSCRIBE_AND_SAVE_PRICE;

      const defaultOmegaUnitPrice = hasAG1ForBundle
        ? OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE
        : OMEGA_SUBSCRIBE_AND_SAVE_PRICE;

      const omegaTotal = Math.round(defaultOmegaUnitPrice * qty);
      const agzTotal = Math.round(defaultAGZUnitPrice * qty);
      const ag1PouchorPackTotal = Math.round(
        AG1_POUCH_OR_TRAVEL_PACK_OTP_SUBSCRIBE_AND_SAVE_PRICE * qty
      );

      if (isAG1PouchorPackOTP) {
        return t('subscribeAndSaveBundle', {
          amount: formatMoney(ag1PouchorPackTotal, currency, { hideDecimalsForWholeNumbers: true })
        });
      }
      return isAGZOTPProduct
        ? t('subscribeAndSaveBundle', {
            amount: formatMoney(agzTotal, currency, { hideDecimalsForWholeNumbers: true })
          })
        : t('subscribeAndSaveBundle', {
            amount: formatMoney(omegaTotal, currency, { hideDecimalsForWholeNumbers: true })
          });
    },
    [hasAG1ForBundle, isAGZOTPProduct, isAG1PouchorPackOTP, t, formatMoney]
  );

  return (
    <div className={styles.itemContainer}>
      {isDebugMode && debugHighlightCartLines.includes(item.id) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 0, 0, 0.2)',
            zIndex: 1
          }}
        />
      )}
      <div className={styles.itemInnerContainer}>
        <div className={styles.imageContainer}>
          <Img
            src={item.merchandise.image?.url}
            alt={item.merchandise.product.title}
            className={styles.itemImage}
            decorative
          />
          {showQuantityBadge && <QuantityBadge quantity={item.quantity} />}
        </div>

        <div className={styles.itemDetails}>
          <Typography variant="body" size="2" weight="700" noPadding className={styles.itemTitle}>
            {item.merchandise.product.title}
          </Typography>
          {isDebugMode && (
            <>
              <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
                Cart Line: {getShopifyVariantId(item.id).slice(0, 8)}...
              </Typography>
              <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
                {getShopifyVariantId(item.merchandise.id)}
              </Typography>
              <Typography
                variant="portalCaption"
                size="1"
                noPadding
                color="grey-1-new"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onMouseOver={() =>
                  checkDebugHighlightProductIds(item.merchandise.id, item.quantity)
                }
                onMouseOut={() => checkDebugHighlightProductIds('', 0)}>
                Check Related Items
              </Typography>
            </>
          )}
          <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
            {description}
          </Typography>

          {showFlavor && (
            <div className={styles.flavorSection}>
              <div
                className={styles.flavorIndicator}
                style={{ backgroundColor: flavorConfig.color }}
              />
              <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
                {flavorConfig.title}
              </Typography>
            </div>
          )}

          {isBundle && (
            <div className={styles.bundleToggleContainer}>
              <Button
                onClick={() => setIsBundleOpen(!isBundleOpen)}
                variant="link"
                size="small"
                noPadding
                icon={isBundleOpen ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                iconPosition="right"
                iconClassName={styles.toggleIcon}
                className={styles.bundleToggle}>
                <Typography variant="portalCaption" size="1" noPadding color="grey-1-new">
                  {BUNDLE_ITEMS.length} {t('bundleToggleSuffix')}
                </Typography>
              </Button>
            </div>
          )}

          {showSubscribeButton && (
            <Button
              onClick={handleSubscribeItem}
              variant="link"
              size="small"
              noPadding
              className={styles.subscribeButton}
              disabled={isCartUpdating}>
              {saveAndSubscribe(item)}
            </Button>
          )}
        </div>
        <div className={styles.rightSection}>
          <div className={styles.priceContainer}>
            <Typography
              variant="caption"
              size="1"
              noPadding
              className={clsx(
                styles.newPrice,
                formatCartPrice(item.cost.totalAmount, hasDiscounts) === freeLabel &&
                  styles.freePrice
              )}>
              {formatCartPrice(item.cost.totalAmount, hasDiscounts)}
            </Typography>
            {showStrikethroughPrice && (
              <Typography variant="caption" size="1" noPadding className={styles.oldPrice}>
                {formatCartPrice(
                  originalPriceMetafield
                    ? {
                        amount: originalPriceMetafield.toString(),
                        currencyCode: item.cost.totalAmount.currencyCode
                      }
                    : item.cost.subtotalAmount,
                  true
                )}
              </Typography>
            )}
          </div>
          {canChangeQuantity && (
            <QuantityEditor
              mode="order"
              max={maxQuantity}
              useTrashIcon
              quantity={item.quantity}
              disabled={isCartUpdating}
              onRemove={() => updateCartItemQuantity(item, 0)}
              onChange={handleQuantityChange}
              onIncrementDisabled={handleIncrementDisabled}
            />
          )}
          {showRemoveButton && (
            <IconButton
              className={styles.deleteButton}
              onClick={() => updateCartItemQuantity(item, 0)}
              ariaLabel="delete"
              disabled={isCartUpdating}>
              <TrashIcon />
            </IconButton>
          )}
        </div>
      </div>

      {/* Error message - positioned 24px under the item */}
      {displayError && (
        <div className={styles.errorMessageContainer}>
          <div className={styles.errorMessageContent}>
            <InfoIcon size={16} color="currentColor" />
            <Typography variant="portalCaption" size="1" noPadding>
              {displayError}
            </Typography>
          </div>
        </div>
      )}

      {isBundle && (
        <Collapse isOpened={isBundleOpen} className={styles.bundleCollapseContainer}>
          <div className={styles.bundleContainer}>
            {BUNDLE_ITEMS.map((bundleItem) => (
              <BundleItem key={bundleItem.id} item={bundleItem} />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  );
};
